import Vue from 'vue'
import { identify, track } from '../libs/rudderstack'

/**
 * DataLayer / Google Tag Manager Directive
 * @desc handles dataLayer events
 * @example <VComponent v-data-layer data-layer="my-event"/>
 * @example <VComponent v-data-layer :data-layer="[ myComputedEvent, myDataEvent ]"/>
 */
export default class DataLayer {
  /**
   * Initializes the DataLayer plugin
   * @desc creates a 'v-data-layer' directive
   */
  static install () {
    Vue.directive('data-layer', { // Create a new directive
      bind: (el, binding, vnode) => {
        // it can be: [object Object], [object String], etc
        const dataType = Object.prototype.toString.call(binding.value)

        el.addEventListener('click', async () => { // Add a click listener
          if (dataType === '[object Function]') {
            binding.value()
            return
          }
          if (dataType === '[object Object]') {
            this.pushObject(binding.value)
            return
          }
          let values = el.dataset.layer
            .split(',')
            .filter(item => item !== '')
            .map((value) => {
              let newValues = value.split(':')
              return {
                key: newValues.length === 2 ? newValues[0] : 'event',
                value: newValues.length === 2 ? newValues[1] : newValues[0]
              }
            })

          try {
            await this.checkContinue(el.dataset.layer, vnode)
            values.forEach(async (item) => {
              this.pushEvent(item.key, item.value) // Push the event
            })
          } catch (error) {
            console.warn(error)
          }
        })
      }
    })
  }

  /**
   * Check Continue
   * @desc check to see if the event contains the string 'continue'
   * If it does, make sure the step is valid before firing the event
   * @param {String} event - datalayer event name
   * @param {Object} vnode - virtual node
   */
  static async checkContinue (event, vnode) {
    if (event.indexOf('continue') !== -1) {
      let error = `Component <${vnode.context.$options.name}> contains invalid fields.`
      if (vnode.context.error) {
        throw error
      }
      try {
        if (vnode.context.stepIsValid === false) {
          throw error
        }
      } catch (error) {
        throw error
      }
    } else {
      return true
    }
  }

  /**
   * Push an event to the dataLayer
   * @desc push the event if it hasn't already been fired
   * @param {String} event - name of the event
   */
  static pushObject (object) {
    if (!object) return
    let eventExists = false
    if (eventExists) return
    dataLayer.push(object)
  }

  static track ({ event, ...properties }) {
    if (!event || !properties) {
      return
    }
    dataLayer.push({
      event,
      properties
    })
    track(event, properties)
  }

  static identify (traits) {
    if (!traits) {
      return
    }

    dataLayer.push({
      event: 'identify()',
      ...traits
    })
    identify(traits)
  }

  /**
   * Push an event to the dataLayer
   * @desc push the event if it hasn't already been fired
   * @param {String} event - name of the event
   */
  static pushEvent (key, value) {
    let found = false // Check if the event has already fired
    for (let i in dataLayer) {
      if (dataLayer[i][key] === value) {
        found = true
        return
      }
    }

    if (!found) { // If the event hasn't fired yet
      dataLayer.push({ [key]: value }) // Push to the dataLayer
    }
  }
}
